import { useEffect, useRef, useState } from 'react';
import './AdminTextos.css';
import './AdminVocabulario.css';

const EMPTY_CAT  = { titulo: '', icon: 'fas fa-book', color: '#1e90ff' };
const EMPTY_WORD = { pt: '', en: '', emoji: '' };

async function apiFetch(path, opts = {}) {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function AdminVocabulario() {
  const [categorias,   setCategorias]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [toast,        setToast]        = useState(null);
  const toastTimer = useRef(null);

  // Form de categoria
  const [catFormOpen,  setCatFormOpen]  = useState(false);
  const [catEditingId, setCatEditingId] = useState(null);
  const [catForm,      setCatForm]      = useState(EMPTY_CAT);
  const [savingCat,    setSavingCat]    = useState(false);
  const [confirmCatId, setConfirmCatId] = useState(null);

  // Categoria expandida + form de palavra
  const [expandedId,   setExpandedId]   = useState(null);
  const [wordForm,     setWordForm]     = useState(EMPTY_WORD);
  const [wordEditingId,setWordEditingId]= useState(null);
  const [savingWord,   setSavingWord]   = useState(false);
  const [confirmWordId,setConfirmWordId]= useState(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }

  async function loadCategorias() {
    setLoading(true);
    try {
      const data = await apiFetch('/api/vocabulario');
      setCategorias(data);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadCategorias(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Categoria ────────────────────────────────────────────
  function openCreateCat() {
    setCatForm(EMPTY_CAT);
    setCatEditingId(null);
    setCatFormOpen(true);
  }

  function openEditCat(cat) {
    setCatForm({ titulo: cat.titulo, icon: cat.icon, color: cat.color });
    setCatEditingId(cat.id);
    setCatFormOpen(true);
  }

  function closeCatForm() {
    setCatFormOpen(false);
    setCatEditingId(null);
  }

  async function submitCat(e) {
    e.preventDefault();
    if (!catForm.titulo.trim()) {
      showToast('O título da categoria é obrigatório.', 'error');
      return;
    }
    setSavingCat(true);
    try {
      if (catEditingId) {
        await apiFetch(`/api/vocabulario/categorias/${catEditingId}`, {
          method: 'PUT',
          body: JSON.stringify(catForm),
        });
        showToast('Categoria atualizada!');
      } else {
        await apiFetch('/api/vocabulario/categorias', {
          method: 'POST',
          body: JSON.stringify(catForm),
        });
        showToast('Categoria criada!');
      }
      closeCatForm();
      loadCategorias();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingCat(false);
    }
  }

  async function deleteCat(id) {
    try {
      await apiFetch(`/api/vocabulario/categorias/${id}`, { method: 'DELETE' });
      setConfirmCatId(null);
      if (expandedId === id) setExpandedId(null);
      showToast('Categoria excluída.');
      loadCategorias();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  // ── Palavras ─────────────────────────────────────────────
  function toggleExpand(cat) {
    setExpandedId(curr => (curr === cat.id ? null : cat.id));
    setWordForm(EMPTY_WORD);
    setWordEditingId(null);
  }

  function openEditWord(word) {
    setWordForm({ pt: word.pt, en: word.en, emoji: word.emoji || '' });
    setWordEditingId(word.id);
  }

  function cancelWordForm() {
    setWordForm(EMPTY_WORD);
    setWordEditingId(null);
  }

  async function submitWord(e, categoriaId) {
    e.preventDefault();
    if (!wordForm.pt.trim() || !wordForm.en.trim()) {
      showToast('Preencha a palavra em português e em inglês.', 'error');
      return;
    }
    setSavingWord(true);
    try {
      if (wordEditingId) {
        await apiFetch(`/api/vocabulario/palavras/${wordEditingId}`, {
          method: 'PUT',
          body: JSON.stringify(wordForm),
        });
        showToast('Palavra atualizada!');
      } else {
        await apiFetch(`/api/vocabulario/categorias/${categoriaId}/palavras`, {
          method: 'POST',
          body: JSON.stringify(wordForm),
        });
        showToast('Palavra adicionada!');
      }
      cancelWordForm();
      loadCategorias();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingWord(false);
    }
  }

  async function deleteWord(id) {
    try {
      await apiFetch(`/api/vocabulario/palavras/${id}`, { method: 'DELETE' });
      setConfirmWordId(null);
      showToast('Palavra excluída.');
      loadCategorias();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="at-page">
      <div className="at-header">
        <h1><i className="fas fa-spell-check" /> Gerenciar Vocabulário</h1>
        <button className="at-btn at-btn-primary" onClick={openCreateCat}>
          <i className="fas fa-plus" /> Nova Categoria
        </button>
      </div>

      <div className="at-stats">
        <span className="at-stat"><i className="fas fa-folder" /> {categorias.length} categorias</span>
        <span className="at-stat"><i className="fas fa-spell-check" /> {categorias.reduce((s, c) => s + c.words.length, 0)} palavras</span>
      </div>

      {/* Form de categoria */}
      {catFormOpen && (
        <div className="at-form-panel">
          <div className="at-form-title">
            <i className={catEditingId ? 'fas fa-edit' : 'fas fa-plus-circle'} />
            {catEditingId ? 'Editar Categoria' : 'Nova Categoria'}
          </div>
          <form onSubmit={submitCat}>
            <div className="at-grid2">
              <div className="at-field">
                <label>Título <span style={{ color: '#ef4444' }}>*</span></label>
                <input
                  type="text"
                  placeholder="Ex: Frutas"
                  value={catForm.titulo}
                  onChange={e => setCatForm(f => ({ ...f, titulo: e.target.value }))}
                  required
                />
              </div>
              <div className="at-field">
                <label>Ícone (Font Awesome)</label>
                <input
                  type="text"
                  placeholder="Ex: fas fa-apple-alt"
                  value={catForm.icon}
                  onChange={e => setCatForm(f => ({ ...f, icon: e.target.value }))}
                />
              </div>
              <div className="at-field">
                <label>Cor</label>
                <div className="av-color-row">
                  <input
                    type="color"
                    value={catForm.color}
                    onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))}
                  />
                  <input
                    type="text"
                    value={catForm.color}
                    onChange={e => setCatForm(f => ({ ...f, color: e.target.value }))}
                  />
                </div>
              </div>
              <div className="at-field" style={{ justifyContent: 'flex-end' }}>
                <label>Pré-visualização</label>
                <div className="av-preview" style={{ '--cat-color': catForm.color }}>
                  <i className={catForm.icon || 'fas fa-book'} />
                  <span>{catForm.titulo || 'Categoria'}</span>
                </div>
              </div>
            </div>

            <div className="at-form-actions">
              <button type="button" className="at-btn at-btn-secondary" onClick={closeCatForm}>Cancelar</button>
              <button type="submit" className="at-btn at-btn-primary" disabled={savingCat}>
                <i className={`fas fa-${savingCat ? 'spinner fa-spin' : 'check'}`} />
                {savingCat ? 'Salvando...' : 'Salvar Categoria'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de categorias */}
      {loading ? (
        <div className="at-empty"><i className="fas fa-spinner fa-spin" /><p>Carregando categorias...</p></div>
      ) : categorias.length === 0 ? (
        <div className="at-empty"><i className="fas fa-inbox" /><p>Nenhuma categoria cadastrada.</p></div>
      ) : (
        categorias.map(cat => (
          <div key={cat.id} className="av-cat-card">
            <div className="av-cat-header" onClick={() => toggleExpand(cat)}>
              <div className="av-cat-info">
                <span className="av-cat-icon" style={{ color: cat.color }}>
                  <i className={cat.icon} />
                </span>
                <span className="av-cat-title">{cat.titulo}</span>
                <span className="av-cat-count">{cat.words.length} palavra{cat.words.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="at-card-actions" onClick={e => e.stopPropagation()}>
                <button className="at-btn at-btn-warning" onClick={() => openEditCat(cat)}>
                  <i className="fas fa-edit" /> Editar
                </button>
                <button className="at-btn at-btn-danger" onClick={() => setConfirmCatId(cat.id)}>
                  <i className="fas fa-trash" /> Excluir
                </button>
                <button className="at-btn at-btn-secondary" onClick={() => toggleExpand(cat)}>
                  <i className={`fas fa-chevron-${expandedId === cat.id ? 'up' : 'down'}`} />
                </button>
              </div>
            </div>

            {expandedId === cat.id && (
              <div className="av-cat-body">
                {cat.words.length > 0 && (
                  <div className="av-word-list">
                    {cat.words.map(w => (
                      <div key={w.id} className="av-word-chip">
                        <span className="av-word-emoji">{w.emoji}</span>
                        <span className="av-word-en">{w.en}</span>
                        <span className="av-word-pt">{w.pt}</span>
                        <button className="av-chip-btn" onClick={() => openEditWord(w)} title="Editar">
                          <i className="fas fa-edit" />
                        </button>
                        <button className="av-chip-btn av-chip-btn-danger" onClick={() => setConfirmWordId(w.id)} title="Excluir">
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <form className="av-word-form" onSubmit={e => submitWord(e, cat.id)}>
                  <input
                    type="text"
                    placeholder="Emoji"
                    value={wordForm.emoji}
                    onChange={e => setWordForm(f => ({ ...f, emoji: e.target.value }))}
                    className="av-word-emoji-input"
                  />
                  <input
                    type="text"
                    placeholder="Palavra em português"
                    value={wordForm.pt}
                    onChange={e => setWordForm(f => ({ ...f, pt: e.target.value }))}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Palavra em inglês"
                    value={wordForm.en}
                    onChange={e => setWordForm(f => ({ ...f, en: e.target.value }))}
                    required
                  />
                  <button type="submit" className="at-btn at-btn-primary" disabled={savingWord}>
                    <i className={`fas fa-${wordEditingId ? 'check' : 'plus'}`} />
                    {wordEditingId ? 'Salvar' : 'Adicionar'}
                  </button>
                  {wordEditingId && (
                    <button type="button" className="at-btn at-btn-secondary" onClick={cancelWordForm}>
                      Cancelar
                    </button>
                  )}
                </form>
              </div>
            )}
          </div>
        ))
      )}

      {/* Confirmar exclusão de categoria */}
      {confirmCatId && (
        <div className="at-modal-backdrop">
          <div className="at-modal">
            <h3>Excluir categoria?</h3>
            <p>Todas as palavras desta categoria também serão excluídas. Esta ação não pode ser desfeita.</p>
            <div className="at-modal-actions">
              <button className="at-btn at-btn-secondary" onClick={() => setConfirmCatId(null)}>Cancelar</button>
              <button className="at-btn at-btn-danger" onClick={() => deleteCat(confirmCatId)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmar exclusão de palavra */}
      {confirmWordId && (
        <div className="at-modal-backdrop">
          <div className="at-modal">
            <h3>Excluir palavra?</h3>
            <p>Esta ação não pode ser desfeita.</p>
            <div className="at-modal-actions">
              <button className="at-btn at-btn-secondary" onClick={() => setConfirmWordId(null)}>Cancelar</button>
              <button className="at-btn at-btn-danger" onClick={() => deleteWord(confirmWordId)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`at-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
